import { cx } from './cx'
import s from './Checklist.module.css'

export interface ChecklistItem {
  key: string
  label: string
  done: boolean
  /** a blocker — rendered with a critical accent when not done */
  critical?: boolean
  /** one-line helper shown under the label */
  hint?: string
  /** right-aligned call-to-action label; fires onAction(key) when clicked */
  action?: string
}

/**
 * A completion checklist (the "getting started" / setup-status list). Each row
 * shows a state glyph (done ✓ · a red dot for an unmet blocker · an open ring for
 * an optional pending), a label + optional hint, and an optional inline action.
 * Controlled + pure: the parent owns the items; the list just renders + emits
 * clicks. Composes the Setup Cockpit but is deliberately generic (onboarding,
 * publish-readiness, any "N of M steps done" surface).
 */
export function Checklist({
  items, onSelect, onAction, className,
}: {
  items: ChecklistItem[]
  onSelect?: (key: string) => void
  onAction?: (key: string) => void
  className?: string
}) {
  /* A CLICKABLE ROW IS A REAL <button> INSIDE THE <li>, NOT AN <li> THAT
     CLAIMS TO BE ONE (#2406 rogue-debt). `role="button"` on the <li> did two
     wrong things at once: it took the <li> out of the list, so axe reports a
     `<ul>` whose children are not list items (`list`, serious — measured on the
     Delivery cockpit, whose gate items are this list); and it put the action
     <button> INSIDE a button, whose children ARIA makes presentational
     (`conformance/fixtures.tsx` carried that as a known failure, #1466). The
     row's control and the action are siblings now, each a real button. */
  return (
    <ul className={cx(s.list, className)}>
      {items.map((it) => {
        const state = it.done ? 'done' : it.critical ? 'blocker' : 'pending'
        const clickable = !!onSelect
        const content = (
          <>
            <span className={s.mark} aria-hidden="true">{it.done ? '✓' : it.critical ? '●' : '○'}</span>
            <span className={s.body}>
              <span className={s.label}>{it.label}</span>
              {it.hint && <span className={s.hint}>{it.hint}</span>}
            </span>
          </>
        )
        return (
          <li key={it.key} className={cx(s.item, s[state], clickable && s.clickable)}
            data-testid={`checkitem-${it.key}`} data-state={state}>
            {clickable
              ? <button type="button" className={s.hit} onClick={() => onSelect!(it.key)}>{content}</button>
              : content}
            {it.action && (
              <button type="button" className={s.action}
                onClick={() => onAction?.(it.key)}>{it.action}</button>
            )}
          </li>
        )
      })}
    </ul>
  )
}
